<template>
  <v-container fluid class="job-gallery">
    <v-layout wrap>
      <v-flex xs12 class="headline">
        <v-btn
          color="primary"
          class="mr-4"
          small
          outlined
          text
          to="/jobs"
          style="margin-top:-3px"
          ><v-icon left>chevron_left</v-icon>Jobs</v-btn
        >
        {{ job.street || "Photo Gallery" }}
        <span class="caption" v-if="job.jobId">Job ID: {{ job.jobId }}</span>
        <v-btn
          class="ml-4"
          x-small
          outlined
          color="primary"
          :to="`/jobs/cost/${jobSiteId}`"
          >View Cost Report</v-btn
        >
      </v-flex>
      <v-flex xs12 class="body-2 grey--text mt-1">{{ counterText }}</v-flex>
    </v-layout>

    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-3 grey--text">Loading photos...</div>
    </div>
    <div v-else-if="loadError" class="text-center red--text py-12">
      There was a problem loading the photos for this job.
    </div>
    <div v-else-if="!firstDate" class="text-center grey--text py-12">
      No daily report photos for this job yet.
    </div>

    <template v-else>
      <!-- TOOLBAR -->
      <div
        ref="toolbar"
        class="gallery-toolbar"
        :class="{ 'gallery-toolbar--sticky': stickyToolbar }"
        :style="stickyToolbar ? { top: `${$vuetify.application.top}px` } : {}"
      >
        <v-btn-toggle v-model="groupBy" mandatory dense color="primary">
          <v-btn small value="day">Day</v-btn>
          <v-btn small value="week">Week</v-btn>
          <v-btn small value="month">Month</v-btn>
        </v-btn-toggle>

        <div class="d-flex align-center">
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="dateRangeText"
                label="Dates"
                placeholder="All dates"
                persistent-placeholder
                prepend-inner-icon="event"
                readonly
                dense
                outlined
                hide-details
                class="gallery-toolbar__dates"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="pickerDates"
              range
              no-title
              :min="firstDate"
              :max="lastDate"
              :picker-date.sync="pickerMonth"
            >
              <v-spacer></v-spacer>
              <v-btn text @click="dateMenu = false">Cancel</v-btn>
              <v-btn
                text
                color="primary"
                :disabled="!pickerDates.length"
                @click="applyDates"
                >OK</v-btn
              >
            </v-date-picker>
          </v-menu>
          <v-btn
            text
            small
            color="primary"
            class="ml-1"
            :disabled="!filtered"
            @click="clearDates"
            >Clear</v-btn
          >
        </div>

        <v-select
          v-model="currentGroup"
          :items="jumpItems"
          label="Jump to"
          dense
          outlined
          hide-details
          :disabled="!groups.length"
          class="gallery-toolbar__jump"
          @change="jumpToGroup"
        ></v-select>

        <v-progress-linear
          v-if="windowLoading"
          indeterminate
          absolute
          bottom
          color="primary"
        ></v-progress-linear>
      </div>
      <!-- END TOOLBAR -->

      <div v-if="windowError" class="text-center red--text py-12">
        There was a problem loading these photos.
      </div>
      <div
        v-else-if="!visiblePhotos.length && !windowLoading && !canLoadMore"
        class="text-center grey--text py-12"
      >
        No photos in this date range.
      </div>

      <!-- PHOTOS. Clicks are handled once here rather than on every tile -->
      <div class="gallery-timeline" @click="onTimelineClick">
        <section
          v-for="group in groups"
          :key="`${groupBy}-${group.key}`"
          :id="`gallery-group-${group.key}`"
          :data-key="group.key"
          class="gallery-group"
        >
          <div class="gallery-group__header">
            <h3 class="title">{{ group.title }}</h3>
            <span class="body-2 grey--text">{{ group.meta }}</span>
          </div>
          <template v-for="day in group.days">
            <div
              v-if="groupBy !== 'day'"
              :key="`${day.date}-header`"
              class="gallery-day"
            >
              {{ day.label
              }}<span class="caption grey--text ml-2">{{ day.countLabel }}</span>
            </div>
            <div :key="`${day.date}-grid`" class="gallery-grid">
              <div
                v-for="(photo, i) in day.photos"
                :key="photo.id"
                class="gallery-tile"
                :data-index="day.start + i"
              >
                <img loading="lazy" :src="photo.thumbnail" alt="" />
                <span class="gallery-tile__date">{{ day.shortLabel }}</span>
                <button
                  type="button"
                  class="gallery-tile__download"
                  title="Download high resolution image"
                >
                  <i class="material-icons">file_download</i>
                </button>
              </div>
            </div>
          </template>
        </section>
      </div>

      <!-- More weeks load automatically when this comes near the screen -->
      <div ref="sentinel" class="gallery-status grey--text">
        <template v-if="loadingMore">
          <v-progress-circular
            indeterminate
            size="20"
            width="2"
            color="primary"
            class="mr-2"
          ></v-progress-circular>
          Loading older photos...
        </template>
        <v-btn
          v-else-if="canLoadMore && photos.length"
          text
          small
          color="primary"
          @click="retryLoadMore"
          >{{
            loadMoreError ? "Couldn't load, try again" : "Load older photos"
          }}</v-btn
        >
        <template v-else-if="!filtered && photos.length">{{ endText }}</template>
      </div>
    </template>

    <!-- LIGHTBOX -->
    <div
      v-if="lightboxPhoto"
      class="gallery-lightbox"
      @click.self="closeLightbox"
      @touchstart.passive="onTouchStart"
      @touchend="onTouchEnd"
    >
      <v-progress-circular
        v-if="lightboxLoading"
        indeterminate
        color="white"
        class="gallery-lightbox__spinner"
      ></v-progress-circular>
      <div class="gallery-lightbox__content">
        <div v-if="lightboxError" class="white--text pa-12">
          Error loading image
        </div>
        <img
          v-else
          :key="lightboxPhoto.id"
          :src="lightboxUrl"
          :class="{ loaded: !lightboxLoading }"
          alt=""
          @load="lightboxLoading = false"
          @error="onLightboxError"
        />
        <div class="gallery-lightbox__bar">
          <span>{{ lightboxCaption }}</span>
          <v-btn small color="primary" @click="download(lightboxPhoto)"
            ><v-icon small left>file_download</v-icon> Download High
            Resolution</v-btn
          >
        </div>
      </div>
      <v-btn
        icon
        large
        dark
        class="gallery-lightbox__close"
        @click="closeLightbox"
        ><v-icon>close</v-icon></v-btn
      >
      <v-btn
        icon
        x-large
        dark
        class="gallery-lightbox__nav gallery-lightbox__nav--prev"
        :disabled="lightboxIndex === 0"
        @click="showLightbox(lightboxIndex - 1)"
        ><v-icon x-large>chevron_left</v-icon></v-btn
      >
      <v-btn
        icon
        x-large
        dark
        class="gallery-lightbox__nav gallery-lightbox__nav--next"
        :disabled="lightboxIndex === visiblePhotos.length - 1 && !canLoadMore"
        @click="showLightbox(lightboxIndex + 1)"
        ><v-icon x-large>chevron_right</v-icon></v-btn
      >
    </div>
    <!-- END LIGHTBOX -->
  </v-container>
</template>

<script>
import fb from "@/main";
import moment from "moment";

// Weeks of photos read per query while scrolling, so big jobs never load all at once
const WINDOW_WEEKS = 4;

// ImageKit serves resized copies of the Firebase Storage originals
const imageKitUrl = (url, transform = "") =>
  url.replace(
    "https://firebasestorage.googleapis.com/",
    `https://ik.imagekit.io/albademo/${transform}`
  );

const pluralize = (count, word) =>
  `${count.toLocaleString()} ${word}${count === 1 ? "" : "s"}`;

const toDateString = (m) => m.format("YYYY-MM-DD");

// Oldest first, and within a day in upload order
const byDateThenUpload = (a, b) =>
  a.date.localeCompare(b.date) || a.uploadedAt - b.uploadedAt;

// The date itself, the Monday its week starts on, or YYYY-MM
function getGroupKey(date, groupBy) {
  if (groupBy === "day") return date;
  if (groupBy === "month") return date.slice(0, 7);
  return toDateString(moment(date).startOf("isoWeek"));
}

function getGroupLabels(key, groupBy) {
  if (groupBy === "day") {
    return {
      title: moment(key).format("dddd, MMMM D, YYYY"),
      range: "",
      short: moment(key).format("ddd, MMM D, YYYY"),
    };
  }
  if (groupBy === "month") {
    const month = moment(`${key}-01`).format("MMMM YYYY");
    return { title: month, range: "", short: month };
  }
  const start = moment(key);
  const end = start.clone().add(6, "days");
  return {
    title: `Week of ${start.format("MMM D, YYYY")}`,
    range: `${start.format("ddd, MMM D")} – ${end.format("ddd, MMM D")}`,
    short: `Week of ${start.format("MMM D, YYYY")}`,
  };
}

export default {
  data() {
    return {
      job: {},
      photos: [], // loaded photos, frozen, oldest first
      firstDate: "", // earliest and latest photo dates on the whole job
      lastDate: "",
      loading: true,
      loadError: false,
      groupBy: "week",
      // A picked date range loads exactly that. With none, the gallery loads
      // the newest WINDOW_WEEKS, then older weeks as you scroll
      rangeFrom: "",
      rangeTo: "",
      loadedFrom: "", // oldest date loaded so far
      loadToken: 0,
      windowLoading: false,
      windowError: false,
      loadingMore: false,
      loadMoreError: false,
      dateMenu: false,
      pickerDates: [],
      pickerMonth: "",
      currentGroup: null,
      lightboxIndex: null,
      lightboxLoading: false,
      lightboxError: false,
      touchStartX: null,
      scrollFrame: null,
    };
  },
  computed: {
    jobSiteId() {
      return this.$route.params.id;
    },
    stickyToolbar() {
      // On a phone the toolbar wraps to several rows, too tall to keep pinned
      return this.$vuetify.breakpoint.mdAndUp;
    },
    filtered() {
      return !!(this.rangeFrom || this.rangeTo);
    },
    // Older weeks left to load
    canLoadMore() {
      if (this.filtered || !this.loadedFrom) return false;
      return this.loadedFrom > this.firstDate;
    },
    // Newest first
    visiblePhotos() {
      return [...this.photos].reverse();
    },
    // Visible photos split into groups, each holding its days in display order.
    // day.start is the index of the day's first photo in visiblePhotos
    groups() {
      const groups = [];
      let group = null;
      let day = null;
      this.visiblePhotos.forEach((photo, index) => {
        if (!day || day.date !== photo.date) {
          const key = getGroupKey(photo.date, this.groupBy);
          if (!group || group.key !== key) {
            group = {
              key,
              ...getGroupLabels(key, this.groupBy),
              days: [],
              count: 0,
            };
            groups.push(group);
          }
          day = {
            date: photo.date,
            label: moment(photo.date).format("dddd, MMM D"),
            shortLabel: moment(photo.date).format("ddd, MMM D, YYYY"),
            start: index,
            photos: [],
          };
          group.days.push(day);
        }
        day.photos.push(photo);
        group.count++;
      });
      groups.forEach((g) => {
        g.meta = [g.range, pluralize(g.count, "photo")]
          .filter(Boolean)
          .join(" · ");
        g.days.forEach((d) => {
          d.countLabel = pluralize(d.photos.length, "photo");
        });
      });
      return groups;
    },
    jumpItems() {
      return this.groups.map((g) => ({
        text: `${g.short} (${g.count.toLocaleString()})`,
        value: g.key,
      }));
    },
    dateRangeText() {
      if (!this.filtered) return "";
      const format = (d) => moment(d).format("MMM D, YYYY");
      if (this.rangeFrom === this.rangeTo) return format(this.rangeFrom);
      return `${this.rangeFrom ? format(this.rangeFrom) : "Start"} – ${
        this.rangeTo ? format(this.rangeTo) : "End"
      }`;
    },
    counterText() {
      if (this.loading || !this.firstDate) return "";
      const format = (d) => moment(d).format("MMM D, YYYY");
      const job = `Job photos run ${format(this.firstDate)} – ${format(
        this.lastDate
      )}`;
      if (!this.photos.length) return job;
      const shown = `${format(this.photos[0].date)} – ${format(
        this.photos[this.photos.length - 1].date
      )}`;
      return `Showing ${pluralize(this.photos.length, "photo")} from ${shown} · ${job}`;
    },
    endText() {
      return `Start of this job's photos (${moment(this.firstDate).format(
        "MMM D, YYYY"
      )})`;
    },
    lightboxPhoto() {
      return this.lightboxIndex === null
        ? null
        : this.visiblePhotos[this.lightboxIndex];
    },
    lightboxUrl() {
      return imageKitUrl(this.lightboxPhoto.url, "tr:w-1600,q-80/");
    },
    lightboxCaption() {
      const total = `${this.visiblePhotos.length.toLocaleString()}${
        this.canLoadMore ? "+" : ""
      }`;
      return `${moment(this.lightboxPhoto.date).format(
        "ddd, MMM D, YYYY"
      )} · ${(this.lightboxIndex + 1).toLocaleString()} of ${total}`;
    },
  },
  watch: {
    groupBy() {
      // Watchers run before the re-render, so the DOM still shows the old
      // grouping here. Note the photo at the top and return to it afterwards
      const anchor = this.getAnchorIndex();
      this.$nextTick(() => {
        if (anchor > 0) this.scrollToIndex(anchor);
        this.updateCurrentGroup();
      });
    },
    dateMenu(open) {
      if (!open) return;
      this.pickerDates = [this.rangeFrom, this.rangeTo].filter(Boolean);
      this.pickerMonth = (this.rangeTo || this.lastDate).slice(0, 7);
    },
  },
  async created() {
    try {
      const [jobDoc] = await Promise.all([
        fb.db
          .collection("jobSites")
          .doc(this.jobSiteId)
          .get(),
        this.fetchDateBounds(),
      ]);
      this.job = jobDoc.exists ? jobDoc.data() : {};
      if (this.firstDate) await this.loadWindow();
    } catch (error) {
      console.log("Error loading job gallery: ", error);
      this.loadError = true;
    }
    this.loading = false;
    this.$nextTick(() => {
      this.updateCurrentGroup();
      this.checkSentinel();
    });
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("keydown", this.onLightboxKey);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  },
  methods: {
    photosQuery() {
      return fb.db
        .collection("dailyReportPhotos")
        .where("jobSiteId", "==", this.jobSiteId)
        .where("photoType", "==", "General Work");
    },
    // Earliest and latest photo dates. Newest first needs the composite index
    // dailyReportPhotos: jobSiteId asc, photoType asc, date desc
    async fetchDateBounds() {
      const [first, last] = await Promise.all([
        this.photosQuery()
          .orderBy("date")
          .limit(1)
          .get(),
        this.photosQuery()
          .orderBy("date", "desc")
          .limit(1)
          .get(),
      ]);
      this.firstDate = first.empty ? "" : first.docs[0].data().date;
      this.lastDate = last.empty ? "" : last.docs[0].data().date;
    },
    async fetchPhotos(from, to) {
      const snap = await this.photosQuery()
        .where("date", ">=", from)
        .where("date", "<=", to)
        .get();
      const photos = [];
      snap.forEach((doc) => {
        const p = doc.data();
        if (!p.url || !/^\d{4}-\d{2}-\d{2}$/.test(p.date || "")) return;
        photos.push({
          id: doc.id,
          date: p.date,
          url: p.url,
          // modifiedDate is not usable for ordering, every photo in an upload
          // batch gets the first file's time. uploadDate at least orders batches
          uploadedAt:
            p.uploadDate && p.uploadDate.toMillis ? p.uploadDate.toMillis() : 0,
        });
      });
      photos.sort(byDateThenUpload);

      // Number photos within their day for download filenames (a window always
      // holds whole days). Frozen so Vue does not make every photo reactive
      const perDay = {};
      return photos.map((p) => {
        perDay[p.date] = (perDay[p.date] || 0) + 1;
        return Object.freeze({
          ...p,
          dayIndex: perDay[p.date],
          thumbnail: imageKitUrl(p.url, "tr:w-300,q-60/"),
        });
      });
    },
    // The WINDOW_WEEKS whole weeks before what is already loaded (or the newest)
    nextWindow() {
      const to = this.loadedFrom
        ? toDateString(moment(this.loadedFrom).subtract(1, "day"))
        : this.lastDate;
      const from = toDateString(
        moment(to)
          .startOf("isoWeek")
          .subtract(WINDOW_WEEKS - 1, "weeks")
      );
      return { from: from < this.firstDate ? this.firstDate : from, to };
    },
    // Replaces what is loaded: the picked date range, or the newest weeks.
    // The token drops a slower earlier load
    async loadWindow() {
      const token = ++this.loadToken;
      this.loadedFrom = "";
      const { from, to } = this.filtered
        ? {
            from: this.rangeFrom || this.firstDate,
            to: this.rangeTo || this.lastDate,
          }
        : this.nextWindow();
      this.windowLoading = true;
      this.windowError = false;
      this.loadMoreError = false;
      try {
        const photos = await this.fetchPhotos(from, to);
        if (token !== this.loadToken) return;
        this.photos = Object.freeze(photos);
        this.loadedFrom = from;
      } catch (error) {
        if (token !== this.loadToken) return;
        console.log("Error loading gallery photos: ", error);
        this.photos = Object.freeze([]);
        this.windowError = true;
      }
      this.windowLoading = false;
      this.$nextTick(() => {
        this.scrollToTimelineTop();
        this.updateCurrentGroup();
        this.checkSentinel();
      });
    },
    // Adds older weeks to the bottom of the timeline
    async loadMore() {
      if (!this.canLoadMore || this.loadingMore) return;
      const token = this.loadToken;
      const { from, to } = this.nextWindow();
      this.loadingMore = true;
      try {
        const more = await this.fetchPhotos(from, to);
        if (token !== this.loadToken) return;
        this.photos = Object.freeze([...more, ...this.photos]);
        this.loadedFrom = from;
      } catch (error) {
        console.log("Error loading more gallery photos: ", error);
        // Stop loading on scroll until retried, or a failing query would repeat
        this.loadMoreError = true;
        return;
      } finally {
        this.loadingMore = false;
      }
      this.$nextTick(this.checkSentinel);
    },
    retryLoadMore() {
      this.loadMoreError = false;
      this.loadMore();
    },
    // Loads more once the bottom of the timeline is within a screen and a half
    checkSentinel() {
      const sentinel = this.$refs.sentinel;
      if (!sentinel || !this.canLoadMore || this.loadingMore) return;
      if (this.loadMoreError) return;
      if (sentinel.getBoundingClientRect().top < window.innerHeight * 2.5) {
        this.loadMore();
      }
    },
    applyDates() {
      const [from, to] = [...this.pickerDates].sort();
      this.rangeFrom = from || "";
      this.rangeTo = to || from || "";
      this.dateMenu = false;
      this.loadWindow();
    },
    clearDates() {
      this.rangeFrom = "";
      this.rangeTo = "";
      this.loadWindow();
    },
    // Height covered at the top of the window: the app bar, plus our toolbar when pinned
    topOffset() {
      const toolbar = this.$refs.toolbar;
      return (
        this.$vuetify.application.top +
        (this.stickyToolbar && toolbar ? toolbar.offsetHeight : 0)
      );
    },
    scrollToElement(element, gap = 12) {
      const top =
        element.getBoundingClientRect().top +
        window.scrollY -
        this.topOffset() -
        gap;
      // Instant on purpose: a smooth scroll lazy loads every thumbnail it passes
      window.scrollTo({ top: Math.max(top, 0) });
    },
    scrollToTimelineTop() {
      const timeline = this.$el.querySelector(".gallery-timeline");
      if (!timeline) return;
      const top =
        timeline.getBoundingClientRect().top + window.scrollY - this.topOffset();
      if (window.scrollY > top) window.scrollTo({ top });
    },
    getCurrentSection() {
      const sections = this.$el.querySelectorAll(".gallery-group");
      const offset = this.topOffset() + 8;
      let current = sections[0] || null;
      for (const section of sections) {
        if (section.getBoundingClientRect().top - offset > 0) break;
        current = section;
      }
      return current;
    },
    // Index of the first photo showing at the top of the screen
    getAnchorIndex() {
      const section = this.getCurrentSection();
      if (!section) return 0;
      const offset = this.topOffset();
      for (const tile of section.querySelectorAll(".gallery-tile")) {
        if (tile.getBoundingClientRect().bottom > offset) {
          return Number(tile.dataset.index);
        }
      }
      return 0;
    },
    scrollToIndex(index) {
      const tile = this.$el.querySelector(`.gallery-tile[data-index="${index}"]`);
      if (!tile) return;
      // In the first row of its day, show the day (or group) heading above it too
      const grid = tile.parentElement;
      const inFirstRow =
        tile.getBoundingClientRect().top ===
        grid.firstElementChild.getBoundingClientRect().top;
      const heading = grid.previousElementSibling;
      this.scrollToElement(inFirstRow && heading ? heading : tile);
    },
    jumpToGroup(key) {
      const section = this.$el.querySelector(`#gallery-group-${key}`);
      if (section) this.scrollToElement(section, 0);
    },
    // Keeps "Jump to" showing the group in view, so it doubles as a "you are here"
    updateCurrentGroup() {
      const section = this.getCurrentSection();
      if (section) this.currentGroup = section.dataset.key;
    },
    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateCurrentGroup();
        this.checkSentinel();
      });
    },
    onTimelineClick(e) {
      const tile = e.target.closest(".gallery-tile");
      if (!tile) return;
      const index = Number(tile.dataset.index);
      if (e.target.closest(".gallery-tile__download")) {
        this.download(this.visiblePhotos[index]);
      } else {
        this.openLightbox(index);
      }
    },
    openLightbox(index) {
      window.addEventListener("keydown", this.onLightboxKey);
      this.showLightbox(index);
    },
    showLightbox(index) {
      if (index < 0) return;
      if (index >= this.visiblePhotos.length) {
        // Paging past the last loaded photo loads the next weeks first
        if (!this.canLoadMore || this.loadingMore) return;
        this.lightboxLoading = true;
        this.loadMore().then(() => {
          if (this.lightboxIndex === null) return;
          if (index < this.visiblePhotos.length) this.showLightbox(index);
          else this.lightboxLoading = false;
        });
        return;
      }
      this.lightboxIndex = index;
      this.lightboxLoading = true;
      this.lightboxError = false;
      // Warm the cache for the neighbours so paging through feels instant
      [index - 1, index + 1].forEach((i) => {
        const photo = this.visiblePhotos[i];
        if (photo) new Image().src = imageKitUrl(photo.url, "tr:w-1600,q-80/");
      });
    },
    closeLightbox() {
      this.lightboxIndex = null;
      window.removeEventListener("keydown", this.onLightboxKey);
    },
    onLightboxError() {
      this.lightboxLoading = false;
      this.lightboxError = true;
    },
    onLightboxKey(e) {
      if (e.key === "Escape") this.closeLightbox();
      else if (e.key === "ArrowLeft") this.showLightbox(this.lightboxIndex - 1);
      else if (e.key === "ArrowRight") this.showLightbox(this.lightboxIndex + 1);
    },
    onTouchStart(e) {
      this.touchStartX = e.touches.length === 1 ? e.touches[0].clientX : null;
    },
    onTouchEnd(e) {
      if (this.touchStartX === null) return;
      const deltaX = e.changedTouches[0].clientX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(deltaX) > 50) {
        this.showLightbox(this.lightboxIndex + (deltaX < 0 ? 1 : -1));
      }
    },
    async download(photo) {
      const url = imageKitUrl(photo.url, "tr:w-2400,h-2400,q-90/");
      // e.g. 26-4869_2026-07-14_003.jpg, numbered within the day so names sort in order
      const filename = `${this.job.jobId || "job"}_${photo.date}_${String(
        photo.dayIndex
      ).padStart(3, "0")}.jpg`;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.statusText);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.log("ImageKit fetch failed, opening in new tab:", error.message);
        window.open(photo.url, "_blank");
      }
    },
  },
};
</script>

<style scoped>
/* Runs edge to edge across the container's 12px padding, with the controls
   padded back in so they line up with the headings below */
.gallery-toolbar {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin: 16px -12px 0;
  padding: 12px;
  background-color: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.gallery-toolbar--sticky {
  position: sticky;
  z-index: 4;
}

.gallery-toolbar__dates {
  width: 270px;
}

.gallery-toolbar__jump {
  max-width: 290px;
}

.gallery-group {
  padding-top: 28px;
}

.gallery-group__header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.gallery-day {
  margin-top: 18px;
  font-size: 15px;
  font-weight: 500;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
  padding-top: 8px;
}

/* Fixed 3:2 tiles keep the layout stable while thumbnails lazy load */
.gallery-tile {
  position: relative;
  aspect-ratio: 3 / 2;
  overflow: hidden;
  border-radius: 4px;
  background-color: #e3e7ee;
  cursor: pointer;
}

.gallery-tile img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease;
}

.gallery-tile:hover img {
  transform: scale(1.04);
}

.gallery-tile__download {
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: flex;
  padding: 3px;
  border: none;
  border-radius: 4px;
  background-color: rgba(21, 72, 158, 0.85);
  color: #fff;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.gallery-tile__download .material-icons {
  font-size: 18px;
}

.gallery-tile:hover .gallery-tile__download,
.gallery-tile__download:focus {
  opacity: 1;
}

/* With 80+ photos a day the day heading is soon off screen, so show it on hover */
.gallery-tile__date {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 2px 6px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 12px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.gallery-tile:hover .gallery-tile__date {
  opacity: 1;
}

.gallery-status {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 24px 0 60px;
  font-size: 14px;
}

.gallery-lightbox {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 210;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.9);
}

.gallery-lightbox__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: calc(100% - 160px);
}

.gallery-lightbox__content img {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 110px);
  object-fit: contain;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.gallery-lightbox__content img.loaded {
  opacity: 1;
}

.gallery-lightbox__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 20px;
  padding-top: 14px;
  color: #fff;
  font-size: 14px;
}

.gallery-lightbox__spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.gallery-lightbox__close {
  position: absolute;
  top: 12px;
  right: 12px;
}

.gallery-lightbox__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.gallery-lightbox__nav--prev {
  left: 16px;
}

.gallery-lightbox__nav--next {
  right: 16px;
}

@media (max-width: 600px) {
  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 8px;
  }

  .gallery-lightbox__content {
    max-width: 100%;
  }

  .gallery-lightbox__nav {
    top: auto;
    bottom: 12px;
    transform: none;
  }
}
</style>
